// The team's spoken lines for the story loop (word/sentence-level TTS — the part Kokoro
// does well). No isolated-phoneme audio anywhere.
//
// Two kinds:
//  • Generic coaching lines (STORY_LINES) — reused every beat.
//  • Per-chapter narration (CHAPTER_LINES) — the voiced "comic spine": an intro card sets
//    the scene when a chapter starts, a victory card celebrates beating its villain. These
//    are LISTENED to (not read), so they can be richer than the decodable beat text — this
//    is how the story stays rich without breaking decodability (see docs/01 rule 3).

import type { LevelId } from './sequence';
import type { Character } from './voices';

export interface SpideyLine {
  id: string;
  text: string;
  /** Whose voice speaks it (defaults to Spidey). */
  character: Character;
}

const line = (id: string, text: string, character: Character = 'spidey'): SpideyLine => ({
  id: `story_${id}`,
  text,
  character,
});

export const STORY_LINES = {
  intro: line('intro', 'A villain scrambled your comic! Read each part, find the word he broke, then sound out the right word to fix it.'),
  found: line('found', 'You found it! Now sound out the right word and build it.'),
  wrong: line('wrong', 'That word is fine. Read the whole part again and keep looking.'),
  fixed: line('fixed', 'Fixed! On to the next part.'),
  complete: line('complete', 'You fixed the whole comic! The city can read again. You cracked the code!'),
} as const;

/** The voiced comic spine — one intro + one victory line per chapter (= phonics level).
 *  chapters.ts references these by id; build-audio synthesizes them in each speaker's voice. */
export const CHAPTER_LINES: Record<LevelId, { intro: SpideyLine; victory: SpideyLine }> = {
  cvc: {
    intro: line('cvc_in', "Hey, web-head here! The Green Goblin is up on the rooftops scrambling every word in the city. Read with me and we'll un-scramble them!", 'spidey'),
    victory: line('cvc_win', "Boom! You fixed every word and the Goblin is out of tricks. The rooftops are safe — awesome work, partner!", 'spidey'),
  },
  digraphs: {
    intro: line('dig_in', "Miles here with Spidey! Doc Ock's metal arms jumbled all the signs in the lab. When two letters team up, we can read it and shut him down.", 'miles'),
    victory: line('dig_win', "Yes! Doc Ock's arms are all tied up and the lab is fixed. You and your reading saved it!", 'miles'),
  },
  blends: {
    intro: line('bl_in', "It's Miles! The Rhino is smashing down the street and crushing letters together. Read the words and we'll stop that rhino!", 'miles'),
    victory: line('bl_win', "Whoa, you stopped the Rhino cold! The street is safe again. That was awesome reading!", 'miles'),
  },
  vce: {
    intro: line('vce_in', "Ghost-Spider here! Sandman buried the ends of all the words at the beach. The magic e brings them back to life — let's read!", 'gwen'),
    victory: line('vce_win', "Way to go! The magic e beat Sandman and the beach is clear. You're a real reading hero!", 'gwen'),
  },
  vowel_teams: {
    intro: line('vt_in', "Gwen and Miles on the case! Electro's zaps fused the vowels into teams all over the power plant. Read the teams and short him out!", 'gwen'),
    victory: line('vt_win', "Zap! Electro is powered down and the lights are back on. Your reading did that!", 'gwen'),
  },
  r_controlled: {
    intro: line('rc_in', "Spidey and Ghost-Spider, up high! The Vulture swooped in and stole the r-sounds from the sky. Read them back and ground him!", 'spidey'),
    victory: line('rc_win', "Got him! The Vulture is grounded and the sky is clear. Amazing reading up there!", 'spidey'),
  },
  diphthongs: {
    intro: line('di_in', "This is it, team! Venom rounded up the whole Sinister Six downtown for one last fight. The whole Spider-Team is here — read with us and win!", 'spidey'),
    victory: line('di_win', "You did it! Venom and the whole Sinister Six are beaten. The city can read again — you cracked the code! Spider-Team forever!", 'spidey'),
  },
};

export const ALL_STORY_LINES: SpideyLine[] = [
  ...Object.values(STORY_LINES),
  ...Object.values(CHAPTER_LINES).flatMap((c) => [c.intro, c.victory]),
];
